import React, { useRef, useState } from 'react';
import {
  Button,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from '@material-ui/core';
import {
  ArrowDropDown as ArrowDropDownIcon,
  Close as CloseIcon,
} from '@material-ui/icons';
import PropTypes from 'prop-types';

import styles from './SelectionBar.module.scss';

// Bulk-selection action bar shown above a table when rows are selected.
// Renders a "Bulk Action" dropdown of the provided actions plus a Cancel
// control. Returns null when nothing is selected.
const SelectionBar = ({
  selectedRowsCount,
  selectedRowsData = [],
  actions,
  onAction,
  onClear,
  label = 'Bulk Action',
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  if (!selectedRowsCount) return null;

  const handleToggle = () => setOpen((prev) => !prev);
  const handleClose = (event) => {
    if (anchorRef.current?.contains(event.target)) return;
    setOpen(false);
  };

  return (
    <div className={styles.bulkActionBar}>
      <div className={styles.bulkActionWrapper}>
        <Button
          ref={anchorRef}
          endIcon={<ArrowDropDownIcon />}
          onClick={handleToggle}
          className={styles.bulkActionBtn}
          disableRipple
          data-testid="bulk-action-button"
        >
          {label}
        </Button>

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          transition
          disablePortal
          placement="bottom-start"
          className={styles.bulkActionPopper}
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps}>
              <Paper className={styles.bulkActionMenu}>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList autoFocusItem={open}>
                    {actions.map((action) => (
                      <MenuItem
                        key={action.key}
                        onClick={(e) => {
                          onAction(action.key, selectedRowsData);
                          handleClose(e);
                        }}
                        className={styles.bulkActionMenuItem}
                        data-testid={`bulk-action-${action.key}`}
                      >
                        {action.icon}
                        {action.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </div>
      <Typography component="span" className={styles.divider} />
      <Button
        onClick={onClear}
        className={styles.selectionBarCancelBtn}
        startIcon={<CloseIcon fontSize="small" />}
        disableRipple
        data-testid="bulk-action-cancel"
      >
        Cancel
      </Button>
    </div>
  );
};

SelectionBar.propTypes = {
  selectedRowsCount: PropTypes.number.isRequired,
  selectedRowsData: PropTypes.array,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      icon: PropTypes.node,
    }),
  ).isRequired,
  onAction: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  label: PropTypes.string,
};

export default SelectionBar;
